import React, { memo } from 'react';

const FormInput = memo(({ 
  label, 
  type = 'text', 
  value, 
  onChange, 
  placeholder, 
  error, 
  required = false, 
  disabled = false,
  className = '',
  ...props 
}) => {
  const inputClasses = `
    w-full border rounded-lg px-4 py-2.5 text-sm transition-all duration-200
    ${error 
      ? 'border-theme-error focus:ring-2 focus:ring-theme-error focus:border-theme-error bg-theme-error/5' 
      : disabled
        ? 'border-theme-border/30 bg-gray-50 text-theme-text cursor-not-allowed'
        : 'border-theme-border/50 focus:ring-2 focus:ring-theme-border focus:border-theme-border bg-white hover:border-theme-border'
    }
  `;

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-theme-heading mb-2">
          {label}
          {required && <span className="text-theme-error ml-1">*</span>}
        </label>
      )}
      
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`${inputClasses} resize-none`}
          {...props}
        />
      ) : type === 'select' ? (
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={inputClasses}
          {...props}
        >
          {props.children}
        </select>
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClasses}
          {...props}
        />
      )}
      
      {error && (
        <p className="mt-1 text-xs text-theme-error flex items-center">
          <span className="mr-1">⚠️</span>
          {error}
        </p>
      )}
    </div>
  );
});

const FormSelect = ({ label, options, value, onChange, error, required = false, disabled = false, className = '', ...props }) => {
  return (
    <FormInput
      label={label}
      type="select"
      value={value}
      onChange={onChange}
      error={error}
      required={required}
      disabled={disabled}
      className={className}
      {...props}
    >
      {options?.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </FormInput>
  );
};

const FormTextarea = ({ label, value, onChange, placeholder, error, required = false, disabled = false, className = '', rows = 3, ...props }) => {
  return (
    <FormInput
      label={label}
      type="textarea"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      error={error}
      required={required}
      disabled={disabled}
      className={className}
      rows={rows}
      {...props}
    />
  );
};

export { FormInput, FormSelect, FormTextarea };
